<script>
import Layout from '@/components/layout/app-layout'
import appConfig from '@/app.config'
import PageHeader from '@/components/layout/page-header'
import tableMixin from '@/mixins/table'
import api from '@/helpers/api'
import { authComputed } from '@/state/helpers'
import {
  recentOrders,
  withDevOrdersPage
} from '@/router/views/dashboards/shared/dev-data'

const STATUS_FILTERS = ['open', 'processing', 'complete']

const STATUS_TONES = {
  open: { color: '#74788d', soft: 'rgba(116, 120, 141, 0.12)' },
  processing: { color: '#f1b44c', soft: 'rgba(241, 180, 76, 0.16)' },
  complete: { color: '#34c38f', soft: 'rgba(52, 195, 143, 0.14)' },
  cancelled: { color: '#94a3b8', soft: 'rgba(148, 163, 184, 0.16)' }
}

/**
 * my orders list Component
 */
export default {
  name: 'sales.web-orders.list',
  page: {
    title: 'Web Orders',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  components: {
    Layout,
    PageHeader
  },
  computed: {
    ...authComputed,
    isAdmin () {
      return this.userRole === 'admin'
    },
    breadcrumbs () {
      return [
        {
          text: 'My Orders',
          active: true
        }
      ]
    },
    activeStatus () {
      return this.$route.query.status || null
    },
    statusFilters () {
      return [
        {
          key: null,
          label: this.$t('my_orders.filter_all'),
          icon: null,
          style: null
        },
        ...STATUS_FILTERS.map(key => {
          const tone = STATUS_TONES[key]
          return {
            key,
            label: this.$t(`web_form_status.${key}.text`),
            icon: this.$t(`web_form_status.${key}.icon`),
            style: {
              '--chip-color': tone.color,
              '--chip-soft': tone.soft
            }
          }
        })
      ]
    },
    subtitle () {
      if (this.activeStatus) {
        return this.$t('my_orders.subtitle_filtered', {
          status: this.$t(`web_form_status.${this.activeStatus}.text`)
        })
      }
      return this.$t('my_orders.subtitle')
    }
  },

  mixins: [tableMixin],
  data () {
    return {
      title: 'My Orders',
      isLoading: false,
      search: null
    }
  },
  created () {
    this.onRefresh()
  },
  watch: {
    search: 'onRefresh',
    currentPage: 'onRefresh',
    perPage: 'onRefresh',
    '$route.query': {
      handler () {
        if (this.currentPage !== 1) {
          this.currentPage = 1
        } else {
          this.onRefresh()
        }
      }
    }
  },
  methods: {
    async onRefresh () {
      this.isLoading = true
      let filters = [{ is_submitted: { _eq: true } }]

      if (this.$route.query.status) {
        filters = [
          ...filters,
          { status: { _eq: this.$route.query.status } }
        ]
      }

      let livePage = { items: [], total_rows: 0 }
      try {
        livePage = await api.webForms.fetchPage({
          page: this.currentPage,
          pageSize: this.perPage === 'All' ? -1 : this.perPage,
          filters,
          needle: this.search,
          orderBy: { submitted_at: 'desc' }
        })
      } catch (e) {
        livePage = { items: [], total_rows: 0 }
      }

      const { items, total_rows } = withDevOrdersPage(livePage, recentOrders, {
        status: this.$route.query.status || null,
        needle: this.search,
        page: this.currentPage,
        pageSize: this.perPage === 'All' ? -1 : this.perPage
      })

      this.data = items
      this.totalRows = total_rows
      this.isLoading = false
    },
    setStatusFilter (status) {
      const query = { ...this.$route.query }
      if (!status) {
        delete query.status
      } else {
        query.status = status
      }
      this.$router.push({ name: 'sales.my-orders', query })
    },
    openOrder (item) {
      if (String(item.id).startsWith('dev-')) return
      this.$router.push({
        name: 'sales.my-orders.view',
        params: { id: item.id }
      })
    },
    referenceOf (item) {
      return item.reference || this.$t('no reference')
    },
    itemOf (item) {
      return (item.item && item.item.short_description) || this.$t('my_orders.dash')
    },
    modelOf (item) {
      if (!item.form_data || !item.form_data.model_type) return null
      return this.$t(`attribute.model_type.${item.form_data.model_type}`)
    },
    foamOf (item) {
      if (!item.form_data || !item.form_data.foam_type) return null
      return this.$t(`attribute.foam_type_short.${item.form_data.foam_type}`)
    },
    isMixFoam (item) {
      return !!(item.form_data && item.form_data.foam_type === 'mix')
    },
    companyOf (item) {
      return (
        (item.customer && item.customer.company_name) ||
        this.$t('my_orders.dash')
      )
    },
    shippingOf (item) {
      if (item.shipping_address) {
        return `${item.shipping_address.company_name}, ${item.shipping_address.city}`
      }
      return this.$t('no shipping')
    },
    quantityOf (item) {
      return item.quantity != null ? item.quantity : this.$t('my_orders.dash')
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbs" />

    <div class="portal-orders">
      <div class="portal-card portal-card--panel">
        <div class="portal-orders__header">
          <div>
            <div class="portal-card__eyebrow mb-1">
              {{ $t('my_orders.eyebrow') }}
            </div>
            <div class="portal-card__panel-title">{{ subtitle }}</div>
          </div>

          <div class="portal-orders__toolbar">
            <g-search-input
              v-model="search"
              :placeholder="$t('my_orders.search_placeholder')"
              width="260px"
              class="portal-orders__search"
            />
            <b-btn
              variant="link"
              size="sm"
              class="p-0 portal-orders__refresh"
              @click="onRefresh"
            >
              <i class="fas fa-sync"></i>
            </b-btn>
          </div>
        </div>

        <div class="portal-orders__filters">
          <button
            v-for="filter in statusFilters"
            :key="filter.key || 'all'"
            type="button"
            class="portal-orders__chip"
            :class="{
              'is-active': activeStatus === filter.key,
              'has-status': !!filter.key
            }"
            :style="filter.style"
            @click="setStatusFilter(filter.key)"
          >
            <i v-if="filter.icon" :class="filter.icon" class="portal-orders__chip-icon"></i>
            <span>{{ filter.label }}</span>
          </button>
        </div>

        <div v-if="isLoading" class="text-muted small py-4">
          {{ $t('Loading...') }}
        </div>

        <div v-else-if="!data.length" class="text-muted small py-4">
          {{ $t('my_orders.empty') }}
        </div>

        <div v-else class="order-table-wrap">
          <div class="order-table" role="table">
            <div class="order-table__head" role="row">
              <div class="order-table__col order-table__col--date" role="columnheader">
                {{ $t('my_orders.col.date') }}
              </div>
              <div class="order-table__col order-table__col--reference" role="columnheader">
                {{ $t('my_orders.col.reference') }}
              </div>
              <div class="order-table__col order-table__col--item" role="columnheader">
                {{ $t('my_orders.col.item') }}
              </div>
              <div class="order-table__col order-table__col--shipping" role="columnheader">
                {{ $t('my_orders.col.shipping') }}
              </div>
              <div class="order-table__col order-table__col--qty" role="columnheader">
                {{ $t('my_orders.col.qty') }}
              </div>
              <div class="order-table__col order-table__col--status" role="columnheader">
                {{ $t('my_orders.col.status') }}
              </div>
              <div class="order-table__col order-table__col--action" role="columnheader"></div>
            </div>

            <button
              v-for="item in data"
              :key="item.id"
              type="button"
              class="order-table__row"
              role="row"
              @click="openOrder(item)"
            >
              <div class="order-table__col order-table__col--date" role="cell" data-label="Date">
                <span class="order-table__primary">
                  {{ item.submitted_at | moment('DD MMM YYYY') }}
                </span>
              </div>

              <div
                class="order-table__col order-table__col--reference"
                role="cell"
                data-label="Reference"
              >
                <div class="order-table__ref">
                  <span class="order-table__primary">{{ referenceOf(item) }}</span>
                  <span
                    v-if="item.form_data && item.form_data.is_remill"
                    class="order-table__flag"
                    :title="$t('Remill')"
                  >
                    <i class="mdi mdi-alpha-r-box"></i>
                  </span>
                  <span
                    v-if="item.file"
                    class="order-table__flag"
                    :title="item.file.file_name"
                  >
                    <i class="mdi mdi-file-check-outline"></i>
                  </span>
                </div>
              </div>

              <div class="order-table__col order-table__col--item" role="cell" data-label="Item">
                <div class="order-table__item">
                  <span class="order-table__primary">{{ itemOf(item) }}</span>
                  <div class="order-table__item-meta">
                    <span v-if="modelOf(item)" class="order-table__muted">{{
                      modelOf(item)
                    }}</span>
                    <span
                      v-if="foamOf(item)"
                      :id="`ttFoamType${item.id}`"
                      class="order-table__foam"
                      :class="{ 'order-table__foam--mix': isMixFoam(item) }"
                    >{{ foamOf(item) }}</span>
                    <b-tooltip
                      v-if="isMixFoam(item)"
                      :target="`ttFoamType${item.id}`"
                      variant="dark"
                      placement="top"
                      container="body"
                      custom-class="text-left"
                    >
                      <p class="mx-2 my-0">
                        {{ $t('Seat') }}:
                        {{
                          $t(
                            `attribute.foam_type.${item.form_data.mix_seat_foam_type}`
                          )
                        }}<br />
                        {{ $t('Back') }}:
                        {{
                          $t(
                            `attribute.foam_type.${item.form_data.mix_back_foam_type}`
                          )
                        }}
                      </p>
                    </b-tooltip>
                  </div>
                </div>
              </div>

              <div
                class="order-table__col order-table__col--shipping"
                role="cell"
                :data-label="
                  isAdmin ? $t('my_orders.col.company') : $t('my_orders.col.shipping')
                "
              >
                <div v-if="isAdmin" class="order-table__stack">
                  <span class="order-table__primary">{{ companyOf(item) }}</span>
                  <span class="order-table__muted">{{ shippingOf(item) }}</span>
                </div>
                <span v-else class="order-table__muted">{{ shippingOf(item) }}</span>
              </div>

              <div class="order-table__col order-table__col--qty" role="cell" data-label="Qty">
                <span class="order-table__primary">{{ quantityOf(item) }}</span>
              </div>

              <div
                class="order-table__col order-table__col--status"
                role="cell"
                data-label="Status"
              >
                <app-status-badge :status="item.status" />
              </div>

              <div class="order-table__col order-table__col--action" role="cell">
                <span class="order-table__action">
                  <i class="mdi mdi-arrow-right"></i>
                </span>
              </div>
            </button>
          </div>
        </div>

        <div
          v-if="totalRows > minPerPage || (perPage !== 'All' && totalRows > perPage)"
          class="portal-orders__footer"
        >
          <div class="portal-orders__page-size" v-if="totalRows > minPerPage">
            <label class="d-flex align-items-center m-0">
              {{ $t('my_orders.show') }}
              <b-form-select
                v-model="perPage"
                size="sm"
                :options="pageOptions"
                class="mx-2"
              ></b-form-select>
            </label>
          </div>

          <b-pagination
            v-if="perPage !== 'All' && totalRows > perPage"
            class="pagination mb-0 justify-content-end"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            size="sm"
          >
            <template v-slot:page="{ page, active }">
              <b v-if="active">{{ page }}</b>
              <i v-else>{{ page }}</i>
            </template>
          </b-pagination>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
.portal-orders {
  margin-bottom: 1.5rem;
}

.portal-card--panel {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  padding: 1.15rem 1.25rem;
}

.portal-card__eyebrow {
  color: #94a3b8;
  font-size: 0.8rem;
  font-weight: 500;
}

.portal-card__panel-title {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 600;
}

.portal-orders__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.portal-orders__toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.portal-orders__refresh {
  color: #64748b;
}

.portal-orders__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.portal-orders__chip {
  appearance: none;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  border-radius: 999px;
  padding: 0.35rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
    color: #0f172a;
  }

  &.has-status {
    color: var(--chip-color);
    border-color: var(--chip-color);
    background: var(--chip-soft);

    &:hover {
      color: var(--chip-color);
      border-color: var(--chip-color);
      background: var(--chip-soft);
      filter: brightness(0.97);
    }
  }

  &.is-active {
    background: #0f172a;
    border-color: #0f172a;
    color: #fff;

    &.has-status {
      background: var(--chip-color);
      border-color: var(--chip-color);
      color: #fff;
    }
  }
}

.portal-orders__chip-icon {
  font-size: 1rem;
  line-height: 1;
}

.order-table-wrap {
  overflow-x: auto;
  margin: 0 -0.15rem;
}

.order-table {
  min-width: 780px;
}

.order-table__head,
.order-table__row {
  display: grid;
  grid-template-columns:
    7.5rem
    minmax(8.5rem, 1.1fr)
    minmax(12rem, 1.8fr)
    minmax(8rem, 1.2fr)
    3.25rem
    8rem
    2.25rem;
  gap: 0.75rem;
  align-items: center;
  padding: 0.75rem 0.15rem;
}

.order-table__head {
  border-bottom: 1px solid #eef2f7;
  color: #94a3b8;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.order-table__row {
  appearance: none;
  width: 100%;
  border: 0;
  border-bottom: 1px solid #eef2f7;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    .order-table__primary {
      color: #556ee6;
    }
  }
}

.order-table__primary {
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 500;
}

.order-table__muted {
  color: #64748b;
  font-size: 0.84rem;
}

.order-table__foam {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 3.25rem;
  padding: 0.15rem 0.4rem;
  border-radius: 0.4rem;
  background: #f1f5f9;
  color: #0f172a;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.order-table__foam--mix {
  cursor: help;
}

.order-table__stack {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 0.15rem;
}

.order-table__item {
  min-width: 0;
}

.order-table__item-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.2rem;
}

.order-table__ref {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 0;
}

.order-table__flag {
  color: #556ee6;
  font-size: 1rem;
  line-height: 1;
  flex: 0 0 auto;
}

.order-table__col--qty {
  text-align: center;
}

.order-table__col--status,
.order-table__col--action {
  display: flex;
  justify-content: flex-end;
}

.order-table__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 0.5rem;
  background: #f1f5f9;
  color: #0f172a;
}

.portal-orders__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1rem;
  padding-top: 0.85rem;
  border-top: 1px solid #eef2f7;
}

.portal-orders__page-size {
  color: #64748b;
  font-size: 0.85rem;
}

@media (max-width: 991.98px) {
  .order-table {
    min-width: 0;
  }

  .order-table__head {
    display: none;
  }

  .order-table__row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.35rem 0.75rem;
    padding: 0.95rem 0.15rem;
  }

  .order-table__col {
    display: none;
  }

  .order-table__col--reference,
  .order-table__col--item,
  .order-table__col--date,
  .order-table__col--shipping,
  .order-table__col--qty {
    display: block;
    grid-column: 1;
  }

  .order-table__col--status {
    display: flex;
    grid-column: 2;
    grid-row: 1 / span 2;
    align-items: flex-start;
  }

  .order-table__col--action {
    display: flex;
    grid-column: 2;
    grid-row: 3;
    align-items: center;
  }

  .order-table__col--reference .order-table__primary {
    font-size: 0.95rem;
  }

  .order-table__col--item::before,
  .order-table__col--date::before,
  .order-table__col--shipping::before,
  .order-table__col--qty::before {
    content: attr(data-label);
    display: inline-block;
    min-width: 4.5rem;
    margin-right: 0.35rem;
    color: #94a3b8;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .order-table__col--item::before {
    vertical-align: top;
    margin-top: 0.15rem;
  }

  .order-table__col--item .order-table__item {
    display: inline-block;
    vertical-align: top;
  }

  .order-table__col--qty {
    text-align: left;
  }
}
</style>
